import { useReactiveVar } from '@apollo/client'
import {
  CameraEnhanceOutlined as CameraEnhanceOutlinedIcon,
  Close as CloseIcon,
  SortOutlined as SortOutlinedIcon,
  PhotoAlbumOutlined as PhotoAlbumOutlinedIcon,
  ShareOutlined as ShareOutlinedIcon
} from '@mui/icons-material'
import { Box, Button, Dialog, IconButton, styled, Typography } from '@mui/material'
import { useState, useMemo } from 'react'
import { modalVar, upsellModalTitleVar, upsellModalButtonTextVar } from '../../../../providers/apollo/cache'
import PaymentModal from './PaymentModal'

// Styled components for the modal
const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    overflow: 'hidden',
    maxWidth: '550px',
    width: '100%',
    margin: '16px',
    '@media (min-width: 768px)': {
      maxWidth: '600px'
    }
  }
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  background: theme.palette.groupsy.gradient,
  padding: '125px 24px 40px',
  position: 'relative',
  textAlign: 'center',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-1px',
    left: 0,
    right: 0,
    height: '70px',
    width: '100%',
    backgroundImage: 'url(https://content.photobucket.com/img/freePlan/freemium-modal-curve.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'bottom center',
    backgroundRepeat: 'no-repeat'
  }
}))

const ConfettiContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 10
})

const ContentSection = styled(Box)({
  background: '#29344A',
  padding: '32px 60px',
  color: 'white',
  marginTop: '-1px',
  '@media (max-width: 600px)': {
    padding: '32px 24px'
  }
})

const FeatureGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  margin: '4px 0 4px 0',
  width: '100%',
  padding: '0',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
})

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
})

const FeatureIcon = styled(Box)(() => ({
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white'
}))

const UpgradeButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  padding: '12px 32px',
  fontSize: '16px',
  fontWeight: 'bold',
  textTransform: 'none',
  width: '100%',
  marginTop: '8px',
  background: theme.palette.groupsy.gradient,
  '&:hover': {
    background: theme.palette.groupsy.gradient
  }
}))

const CancelText = styled(Typography)({
  textAlign: 'center',
  marginTop: '12px',
  fontSize: '14px',
  opacity: 0.8,
  color: 'white'
})

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  color: 'black',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'transparent'
  }
})

export default function UpsellModal() {
  const modal = useReactiveVar(modalVar)
  const modalTitle = useReactiveVar(upsellModalTitleVar)
  const buttonText = useReactiveVar(upsellModalButtonTextVar)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const UPGRADE_PRICE = '8.00'

  // Memoize the formatted date to avoid creating new Date() on every render
  const paymentDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }, [])

  const handleClose = () => {
    modalVar(null)
    setShowPaymentModal(false)
  }

  const handleUpgrade = () => {
    setShowPaymentModal(true)
    modalVar(null)
  }

  const handlePaymentClose = () => {
    setShowPaymentModal(false)
  }

  const features = [
    { icon: 'PhotoAlbumOutlinedIcon', text: 'Unlimited buckets for all your collections.' },
    { icon: 'ShareOutlinedIcon', text: 'Share with as many groups as you like.' },
    { icon: 'SortOutlinedIcon', text: 'Advanced sorting tools.' },
    { icon: 'CameraEnhanceOutlinedIcon', text: 'Ample space for every memory.' }
  ]

  return (
    <>
      <StyledDialog open={modal === 'upsellModal'} onClose={handleClose}>
        <HeaderSection>
          <CloseButton onClick={handleClose} size="small">
            <CloseIcon />
          </CloseButton>

          <ConfettiContainer>
            <img
              src="https://content.photobucket.com/img/freePlan/crown.png"
              alt="Crown"
              style={{
                width: '160px',
                position: 'absolute',
                left: '50%',
                bottom: '-10px',
                transform: 'translateX(-50%)'
              }}
            />
          </ConfettiContainer>
        </HeaderSection>
        <ContentSection>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              marginBottom: '24px',
              '@media (max-width: 600px)': {
                fontSize: '1.2rem'
              }
            }}
          >
            {modalTitle.split('\n').map((line, index) => (
              <span key={line}>
                {line}
                {index < modalTitle.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Typography>
          <FeatureGrid>
            {features.map((feature, index) => {
              const iconMap = {
                CameraEnhanceOutlinedIcon,
                SortOutlinedIcon,
                ShareOutlinedIcon,
                PhotoAlbumOutlinedIcon
              }
              const IconComponent = iconMap[feature.icon as keyof typeof iconMap]

              return (
                // eslint-disable-next-line react/no-array-index-key
                <FeatureItem key={`feature-${index}`}>
                  <FeatureIcon>
                    <IconComponent sx={{ fontSize: '16px' }} />
                  </FeatureIcon>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feature.text}
                  </Typography>
                </FeatureItem>
              )
            })}
          </FeatureGrid>
        </ContentSection>
        <Box
          sx={{
            background: '#29344A',
            padding: '0 40px 32px',
            marginTop: '-1px',
            '@media (max-width: 600px)': {
              padding: '0 24px 32px'
            }
          }}
        >
          <UpgradeButton variant="contained" color="primary" onClick={handleUpgrade}>
            {buttonText}
          </UpgradeButton>
          <CancelText>Cancel Anytime</CancelText>
        </Box>
      </StyledDialog>
      <PaymentModal
        open={showPaymentModal}
        onClose={handlePaymentClose}
        planName="sharing"
        billingCycle="monthly"
        total={UPGRADE_PRICE}
        buttonText="Complete Upgrade"
        content={`Your first payment of $${UPGRADE_PRICE} will be on ${paymentDate} and billing will recur on a monthly basis. You can cancel anytime.`}
        source="modal"
      />
    </>
  )
}

// Helper function to open the modal programmatically
export const openUpsellModal = () => {
  modalVar('upsellModal')
}
